import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Tooltip from '@components/Tooltip';
import HelpIcon from '@mui/icons-material/Help';
import IconButton from '@mui/material/IconButton';
import { Input } from '@components/Input';
import ComboLocationSelect from '@features/ComboLocationSelect';
import { Checkbox } from '@components/Checkbox';
import { Typography } from '@components/Typography';
import { useTranslation, useLocations } from '@hooks';

const Container = styled('div', { shouldForwardProp: prop => prop !== 'isEdit' })(({ isEdit }) => ({
    display: 'grid',
    gridGap: '16px 24px',
    gridTemplateAreas: isEdit ? '"input1" "input2" "select1"' : '"input1" "select1" "checkbox"',
    gridTemplateColumns: '100%',
    '@media (max-width: 600px)': {
        gridTemplateAreas: isEdit ? '"input1" "input2" "select1"' : '"input1" "select1" "checkbox"',
        gridGap: '16px',
    },
    '& .combo-location-select': {
        gridArea: 'select1',
        '& .location-type': {
            marginTop: '-16px',
        },
    },
}));

const CheckboxWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    '& .MuiCheckbox-root': {
        padding: 4,
    },
});

const WhatsAppForm = ({ isEdit }) => {
    const brands = useSelector(state => state.brands.data);

    const { t } = useTranslation();
    const { getSelectLocationsOptions } = useLocations();
    const { control, setValue, getValues, selectedChannel } = useFormContext();

    const brandsOptions = useMemo(() => brands.map(item => ({ value: item.id, label: item.title })), [brands]);

    const locationsOptions = useMemo(() => getSelectLocationsOptions(), []);

    return (
        <Container isEdit={isEdit}>
            <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('SettingsChannels.channelName')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                        sx={{ gridArea: 'input1' }}
                    />
                )}
            />
            {isEdit ? (
                <Controller
                    control={control}
                    name="phoneNumber"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsChannels.whatsAppNumber')}
                            fullWidth
                            error={!!error}
                            disabled
                            helperText={error ? error.message : null}
                            sx={{ gridArea: 'input2' }}
                        />
                    )}
                />
            ) : null}
            <ComboLocationSelect
                brandsOptions={brandsOptions}
                locationsOptions={locationsOptions}
                data={isEdit ? selectedChannel : null}
            />
            {!isEdit ? (
                <CheckboxWrapper style={{ gridArea: 'checkbox' }}>
                    <Controller
                        control={control}
                        name="newWhatsAppAccount"
                        render={({ ref }) => (
                            <Checkbox
                                ref={ref}
                                inputProps={{ 'aria-label': 'controlled' }}
                                onChange={e => setValue('newWhatsAppAccount', e.target.checked)}
                                checked={getValues('newWhatsAppAccount')}
                            />
                        )}
                    />
                    <Typography variant="body2" sx={{ marginRight: '8px' }}>
                        {t('SettingsChannels.isNewWhatsAppAcc')}
                    </Typography>
                    <IconButton>
                        <Tooltip title={t('SettingsChannels.whatsAppTooltip')} placement="top" enterTouchDelay={0}>
                            <HelpIcon sx={{ fontSize: '16px' }} />
                        </Tooltip>
                    </IconButton>
                </CheckboxWrapper>
            ) : null}
        </Container>
    );
};

export default WhatsAppForm;
